/**
 * The `companies.technical_identifiers` entry shape, its parsing, and the
 * (system, label) upsert semantics. Moved out of `apps/hub/lib/companies/` so
 * the CRM editor, the Meridian tools, the Slack modals, the provisioning canvas
 * and the ML suggestion loop all share one definition.
 *
 * A company's `technical_identifiers` jsonb column is a STAFF-ONLY (support
 * engineers) extensible array of the technical system identifiers WTC needs to
 * service that client — Microsoft 365 tenant IDs, Azure subscription IDs, AWS
 * account IDs, Datadog org IDs, etc. ONE column, no migration to add a new
 * system type: each entry self-describes its {system, label, value, notes?}.
 *
 * NOT for secrets/credentials (no client secret, API key, or password) — these
 * are directory/account identifiers only.
 */

import { z } from "zod";
import { canonicalizeLabel, normalizeSystemSlug } from "./registry.js";
import type { TechnicalSystemSlug } from "./systems.js";

/** One technical system identifier a support engineer uses to service a company. */
export interface TechnicalIdentifier {
  /** Canonical system slug (e.g. "microsoft_365", "aws"). See `TECHNICAL_SYSTEMS`. */
  system: TechnicalSystemSlug;
  /** Label for the identifier within that system (e.g. "Tenant ID"). */
  label: string;
  /** The identifier value itself (e.g. the tenant UUID / account number). */
  value: string;
  /** Optional free-text note (scope, environment, caveat). */
  notes?: string;
}

const nonEmpty = (field: string) => z.string().trim().min(1, `${field} is required`);

/**
 * Validates a single identifier entry at the API/UI boundary.
 *
 * Normalization happens INSIDE the schema so every path — form submit, Meridian
 * tool, Slack modal, stored-row read — produces the same canonical (system,
 * label) pair. That is what makes the upsert key trustworthy: `m365` + `tenant
 * id` and `Microsoft 365` + `Tenant ID` are the same entry, and always were
 * meant to be.
 *
 * Value shape is NOT validated here on purpose — see `value-formats.ts`.
 */
export const technicalIdentifierSchema = z.object({
  system: nonEmpty("system").transform(normalizeSystemSlug),
  label: nonEmpty("label"),
  value: nonEmpty("value"),
  notes: z.string().trim().optional(),
});

export type TechnicalIdentifierInput = z.input<typeof technicalIdentifierSchema>;

function normalizeEntry(entry: z.output<typeof technicalIdentifierSchema>): TechnicalIdentifier {
  return {
    system: entry.system,
    label: canonicalizeLabel(entry.system, entry.label),
    value: entry.value,
    // Drop empty notes so stored rows stay clean and equality is predictable.
    ...(entry.notes ? { notes: entry.notes } : {}),
  };
}

/**
 * Validate + canonicalize one entry. Returns the normalized identifier, or an
 * actionable message when a required part is missing.
 */
export function parseTechnicalIdentifierInput(
  input: unknown,
): { ok: true; identifier: TechnicalIdentifier } | { ok: false; error: string } {
  const parsed = technicalIdentifierSchema.safeParse(input);
  if (!parsed.success) {
    return {
      ok: false,
      error: parsed.error.issues[0]?.message ?? "system, label, and value are required",
    };
  }
  return { ok: true, identifier: normalizeEntry(parsed.data) };
}

/** Parse a JSON string, returning `null` (never throwing) on invalid JSON. */
function safeJsonParse(raw: string): unknown {
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

/**
 * Parse an untrusted jsonb value (the stored column) into a clean identifier
 * array. Non-arrays become []; malformed entries are dropped rather than
 * throwing, so one bad row never blocks reading or editing the rest.
 *
 * Entries are canonicalized on READ as well as write, so rows written before the
 * registry existed line up with rows written after it without a migration. When
 * canonicalization collapses two legacy rows onto the same (system, label) key
 * the FIRST is kept — upsert replaces in place, so position one holds the most
 * recently written value.
 *
 * IMPORTANT — dual transport. The SAME `technical_identifiers` jsonb column
 * arrives here in two different runtime shapes depending on how it was read:
 *   • PostgREST (`.from("companies").select(...)`) returns a parsed JS array.
 *   • pg_graphql over Apollo (the Meridian get/set/remove executors) returns
 *     the column as a JSON *string* — e.g. `'[{"system":...}]'` — even though
 *     the codegen `JSON` scalar is (mis)typed `Record<string, any>`. Treating
 *     that string as "not an array" silently returned [], which made the read
 *     tool report count:0 AND made every set overwrite the whole array (it
 *     upserted onto []). So we JSON.parse a string transport before inspecting.
 */
export function parseTechnicalIdentifiers(raw: unknown): TechnicalIdentifier[] {
  const value = typeof raw === "string" ? safeJsonParse(raw) : raw;
  if (!Array.isArray(value)) return [];
  const out: TechnicalIdentifier[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    const parsed = technicalIdentifierSchema.safeParse(item);
    if (!parsed.success) continue;
    const entry = normalizeEntry(parsed.data);
    const key = entryKey(entry);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(entry);
  }
  return out;
}

/** Identity of an entry is its canonical (system, label). */
function entryKey(entry: { system: string; label: string }): string {
  return `${normalizeSystemSlug(entry.system)}::${canonicalizeLabel(
    entry.system,
    entry.label,
  ).toLowerCase()}`;
}

function sameKey(
  a: { system: string; label: string },
  b: { system: string; label: string },
): boolean {
  return entryKey(a) === entryKey(b);
}

/**
 * Upsert one identifier keyed by (system, label). Replaces an existing entry
 * with the same canonical key or appends a new one. Returns the next array,
 * whether the entry was newly created (vs. updated in place), and an advisory
 * warning when the value does not look like the shape the field declares.
 */
export function upsertTechnicalIdentifier(
  list: readonly TechnicalIdentifier[],
  entry: TechnicalIdentifier,
): { next: TechnicalIdentifier[]; created: boolean } {
  const idx = list.findIndex((e) => sameKey(e, entry));
  if (idx === -1) return { next: [...list, entry], created: true };
  const next = list.slice();
  next[idx] = entry;
  return { next, created: false };
}

/**
 * Remove identifier(s) matching (system, label). Returns the next array and how
 * many entries were removed (0 = nothing matched).
 */
export function removeTechnicalIdentifier(
  list: readonly TechnicalIdentifier[],
  system: string,
  label: string,
): { next: TechnicalIdentifier[]; removed: number } {
  const key = { system, label };
  const next = list.filter((e) => !sameKey(e, key));
  return { next, removed: list.length - next.length };
}
